/**
 * a2-lp.js — A2 landing pages (templates/collection.a2-discover-1.json and -2.json).
 * Behaviour specs: discover-b-handoff/02-BEHAVIOUR.md (Discover-1) and
 * discover-lp-handoff/02-BEHAVIOUR.md (Discover-2).
 *
 * Each section initialises from its own root, so any section can be removed in the editor:
 *   [data-a2-lp-size]     size question hero (state logic shared with its inline script: window.A2LPSize)
 *   [data-a2-lp-matcher]  Find your Stretch card
 *   [data-a2-lp-press]    press feature / press strip (cycling quotes)
 *   [data-a2-lp-range]    Stretch I / II tabs
 *   [data-a2-lp-sticky]   mobile sticky bar
 * Helpers track(), fill(), localise() and bindSwipe() are copied from assets/a2-pdp.js (not loaded here).
 * Usual size is shared with the A2 product page through localStorage 'ys_usual_size' (US number).
 *
 * Reuse outside the landing pages (the homepage's a2-hp-* sections use the same matcher / press / range
 * markup): a root may carry data-a2-events='{"lp_matcher_size":"hp_size_pick",…,"_extra":{…},"_report":"…"}'
 * to rename its analytics events, add fields to them and set the quiz-reporting source, and
 * data-a2-keep-view="false" to leave its links alone on a ?view= preview. Roots without them behave as before.
 */
(function () {
  'use strict';
  if (window.A2LP) return;

  var LS_USUAL = 'ys_usual_size';
  var SS_STRETCH = 'ys_lp_stretch';
  var mqDesktop = window.matchMedia('(min-width: 1024px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function readCfg(root) {
    if (root._cfg) return root._cfg;
    var el = root.querySelector('script[data-a2-lp-config]');
    try { root._cfg = el ? JSON.parse(el.textContent) : {}; } catch (e) { root._cfg = {}; }
    return root._cfg;
  }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function track(name, data) {
    data = data || {};
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data); } catch (e) {}
  }
  // Per-root event names / extra fields (data-a2-events); unchanged names for the landing pages.
  function evMap(root) {
    if (!root) return {};
    if (root._a2ev) return root._a2ev;
    try { root._a2ev = JSON.parse(root.getAttribute('data-a2-events') || '{}') || {}; } catch (e) { root._a2ev = {}; }
    return root._a2ev;
  }
  function trackR(root, name, data) {
    var m = evMap(root);
    track(m[name] || name, Object.assign({}, data || {}, m._extra || {}));
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  // Sizing comes from the section config (snippets/a2-size-system): US, or UK / AU = US + 4.
  function localise(t, z) {
    z = z || { label: 'US', offset: 0 };
    if (!t || z.label === 'US') return t;
    var n = function (us) { var v = parseInt(us, 10); return isNaN(v) ? us : v + (z.offset || 0); };
    return String(t)
      .replace(/US\s*(\d+)\s*([–-])\s*(\d+)\s*·\s*UK\s*(\d+)\s*[–-]\s*(\d+)/g, function (m, a, d, b, c, e) { return z.label + ' ' + c + d + e; })
      .replace(/\bUS\s*(\d+)\s*([–\-\/])\s*(\d+)/g, function (m, a, d, b) { return z.label + ' ' + n(a) + d + n(b); })
      .replace(/\bUS\s+(\d+)\b/g, function (m, a) { return z.label + ' ' + n(a); })
      .replace(/\bUS(?=\s+(\[size\]|sizes?\b))/g, z.label)
      .replace(/\b([aA]) (AU\b)/g, '$1n $2'); // "an AU 14"
  }
  function smooth() { return reduceMotion.matches ? 'auto' : 'smooth'; }
  function template() {
    var m = /(?:^|[?&])view=([^&#]+)/.exec(location.search);
    if (m) return decodeURIComponent(m[1]);
    return document.querySelector('[data-a2-lp-size]') ? 'a2-discover-1' : 'a2-discover-2';
  }

  /* Keep ?view= on the filtered collection links, so preview / test traffic on an alternate
     template (…?view=a2-discover-1) stays on it after the filter is applied. */
  function keepView(url, root) {
    if (root && root.getAttribute('data-a2-keep-view') === 'false') return url;
    var v = (/(?:^|[?&])view=([^&#]+)/.exec(location.search) || [])[1];
    if (!v || !url || url.charAt(0) === '#') return url;
    try {
      var u = new URL(url, location.href);
      if (u.origin !== location.origin || u.searchParams.has('view')) return url;
      u.searchParams.set('view', decodeURIComponent(v));
      return u.pathname + u.search + u.hash;
    } catch (e) { return url; }
  }
  function keepViewLinks(scope) {
    $$('a[href*="/collections/"]', scope).forEach(function (a) { a.setAttribute('href', keepView(a.getAttribute('href'))); });
  }

  /* ---------- Sticky header offset and in-page anchors (02-BEHAVIOUR §6) ---------- */
  function headerOffset() {
    var h = 0;
    $$('.shopify-section-group-header-group, [data-section-type="header"], .site-header, header').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
      var r = el.getBoundingClientRect();
      if (r.top <= 1 && r.bottom > 0 && r.height < window.innerHeight / 2) h = Math.max(h, r.bottom);
    });
    return Math.round(h);
  }
  function setHeaderVar() {
    var h = headerOffset() + 'px';
    $$('.ys.a2-lp').forEach(function (el) { el.style.setProperty('--lp-header', h); });
  }
  function scrollToId(id, focus) {
    var t = id && document.getElementById(id);
    if (!t) return false;
    setHeaderVar();
    t.scrollIntoView({ behavior: smooth(), block: 'start' });
    if (focus) {
      var f = t.matches('[data-a2-lp-focus], h2[tabindex]') ? t : t.querySelector('[data-a2-lp-focus], h2[tabindex]');
      if (f) setTimeout(function () { f.focus({ preventScroll: true }); }, reduceMotion.matches ? 0 : 600);
    }
    return true;
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('.ys.a2-lp a[href]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
    var href = a.getAttribute('href') || '';
    var hash = href.split('#')[1];
    if (!hash) return;
    var pathPart = href.split('#')[0];
    // Same page (bare #anchor, or a URL equal to this one): scroll instead of reloading.
    if (pathPart) {
      try { var u = new URL(pathPart, location.href); if (u.pathname + u.search !== location.pathname + location.search) return; } catch (err) { return; }
    }
    if (scrollToId(hash, hash === 'how' || a.hasAttribute('data-a2-lp-how'))) e.preventDefault();
  });

  /* ---------- Lazy, on-screen-only videos (snippets/a2-video.liquid) ---------- */
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var videoIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target;
      if (e.isIntersecting) {
        if (saveData || reduceMotion.matches || !v.hasAttribute('data-a2-autoplay')) return;
        if (!v.getAttribute('src')) {
          var src = mqDesktop.matches ? (v.dataset.srcHd || v.dataset.srcSd) : (v.dataset.srcSd || v.dataset.srcHd);
          if (src) v.src = src;
        }
        v.play().catch(function () {});
      } else if (!v.paused) v.pause();
    });
  }, { rootMargin: '100px 0px' }) : null;
  function initVideos(scope) {
    if (!videoIO) return;
    $$('.ys.a2-lp [data-a2-lazy-video]', scope).forEach(function (v) { if (!v._a2lp) { v._a2lp = true; videoIO.observe(v); } });
  }

  /* Swipe (pointer + horizontal trackpad), from a2-pdp.js. cb(+1) = next, cb(-1) = previous. */
  function bindSwipe(el, cb) {
    var x0 = null, y0 = null, pid = null, acc = 0, lockUntil = 0;
    function up(e) {
      window.removeEventListener('pointerup', up);
      if (x0 == null || e.pointerId !== pid) return;
      var dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) { el._a2Swiped = Date.now(); cb(dx < 0 ? 1 : -1); }
    }
    el.addEventListener('pointerdown', function (e) {
      if (e.button > 0) return; x0 = e.clientX; y0 = e.clientY; pid = e.pointerId;
      window.addEventListener('pointerup', up);
    }, { passive: true });
    el.addEventListener('pointercancel', function () { x0 = null; });
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.addEventListener('click', function (e) { if (el._a2Swiped && Date.now() - el._a2Swiped < 350) { e.preventDefault(); e.stopPropagation(); } }, true);
    el.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (Date.now() < lockUntil) return;
      acc += e.deltaX;
      if (Math.abs(acc) > 50) { cb(acc > 0 ? 1 : -1); acc = 0; lockUntil = Date.now() + 650; }
    }, { passive: false });
  }

  /* Reuse the fit quiz's reporting (assets/quiz.js), so sizes picked on the landing pages land
     in the same places as quiz answers:
     - GTM: the quiz's `quiz_answer_recorded` event, with its own question_id so landing-page picks
       can be told apart from (and don't inflate) the quiz's own size question ('size').
     - Orders: the quiz's cart attributes `_quiz_size` (US number; drives the Shopify Flow
       `quiz-size-{n}` order tag) and `_quiz_size_reported` (e.g. "UK14"), plus `_quiz_source`.
       Skipped when the cart already holds a completed quiz (`_quiz_completed_at`), so a real quiz
       result is never overwritten. Debounced: only the last pick is written.
     immediate = true (the sale size bar, which reloads the page to apply the filter): write now and
     return a promise that settles when the write has finished, so the reload doesn't cut it off. */
  var quizCartTimer = null;
  function writeQuizSize(us, questionId, z) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && cart.attributes && cart.attributes._quiz_completed_at) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: {
            _quiz_size: String(us),
            _quiz_size_reported: z.label + (parseInt(us, 10) + (z.offset || 0)),
            _quiz_source: questionId
          } })
        });
      })
      .catch(function () { /* reporting never blocks the page */ });
  }
  function reportSize(us, questionId, sizing, immediate) {
    var z = sizing || { label: 'US', offset: 0 };
    track('quiz_answer_recorded', { question_id: questionId, answer_values: String(us) });
    clearTimeout(quizCartTimer);
    if (immediate) {
      try { return writeQuizSize(us, questionId, z); } catch (e) { return Promise.resolve(); }
    }
    quizCartTimer = setTimeout(function () {
      try { writeQuizSize(us, questionId, z); } catch (e) { /* silent */ }
    }, 800);
    return Promise.resolve();
  }

  function stretchEvent(stretch, us, url, initial) {
    document.dispatchEvent(new CustomEvent('lp:stretch', { detail: { stretch: stretch, usualSizeUs: us, url: url, initial: !!initial } }));
  }

  /* ---------- Size question hero (Discover-1, 02-BEHAVIOUR §1) ---------- */
  function initSizeHero(root) {
    if (!window.A2LPSize) return;
    var cfg = readCfg(root);
    ['Stretch I', 'Stretch II', 'all'].forEach(function (k) { if (cfg.urls && cfg.urls[k]) cfg.urls[k] = keepView(cfg.urls[k]); });
    var cta = $('[data-a2-lp-cta]', root);
    if (cta) cta.setAttribute('href', keepView(cta.getAttribute('href')));
    keepViewLinks(root);

    // Mobile: scroll the model row so the picked size's model is the second visible tile.
    root._scrollModels = function (us, instant) {
      if (mqDesktop.matches) return;
      var set = $('[data-set]:not([hidden])', root); if (!set) return;
      var tiles = $$('.a2-lp-sh__tile', set), i = -1;
      tiles.forEach(function (t, k) { if (t.getAttribute('data-size') === String(us)) i = k; });
      if (i < 0) return;
      var step = tiles[0].getBoundingClientRect().width + 4;
      var x = Math.max(0, Math.min((i - 1) * step, set.scrollWidth - set.clientWidth));
      set.scrollTo({ left: x, behavior: instant ? 'auto' : smooth() });
    };
    if (root.dataset.size) {
      root._scrollModels(root.dataset.size, true);
      stretchEvent(root.dataset.stretch, root.dataset.size, cfg.urls[root.dataset.stretch], true);
    }

    // The inactive model set is lazy (hidden). Warm it once the shopper reaches for the chips,
    // or when the page is idle, so a Stretch switch shows photos straight away.
    var warmed = false;
    function warm() {
      if (warmed) return; warmed = true;
      $$('[data-set][hidden] img[loading="lazy"]', root).forEach(function (img) { img.loading = 'eager'; });
    }
    var chipsEl = $('.a2-lp-sh__chips', root);
    if (chipsEl) ['pointerenter', 'touchstart', 'focusin'].forEach(function (ev) { chipsEl.addEventListener(ev, warm, { once: true, passive: true }); });
    window.addEventListener('load', function () { setTimeout(function () { (window.requestIdleCallback || setTimeout)(warm); }, 2500); });

    root.addEventListener('click', function (e) {
      var chip = e.target.closest('.a2-lp-sh__chip');
      if (chip) {
        if (chip.getAttribute('aria-pressed') === 'true') return; // no deselect
        var us = chip.getAttribute('data-size'), firstPick = !root.dataset.size;
        var st = window.A2LPSize.apply(root, us, !reduceMotion.matches);
        if (!st) return;
        lsSet(LS_USUAL, us);
        stretchEvent(st, us, cfg.urls[st], false);
        track('lp_size_pick', { usual_size_us: us, stretch: st, first_pick: firstPick });
        reportSize(us, 'discover_size_hero', cfg.sizing);
        return;
      }
      if (e.target.closest('[data-a2-lp-cta]')) {
        ssSet(SS_STRETCH, root.dataset.stretch || '');
        track('lp_hero_cta', { stretch: root.dataset.stretch, usual_size_us: root.dataset.size, method: cfg.urls && cfg.urls.source === 'filter' ? 'navigate' : 'fallback' });
      } else if (e.target.closest('[data-a2-lp-how]')) {
        track('lp_hero_how', { state: root.dataset.size ? 'picked' : 'start' });
      } else if (e.target.closest('[data-a2-lp-shop-all]')) {
        track('lp_hero_shop_all');
      }
    });
    track('lp_view', { template: template(), initial_state: root.dataset.initial || 'start' });
  }

  /* ---------- Find your Stretch matcher (Discover-2, 02-BEHAVIOUR §1) ---------- */
  function initMatcher(root) {
    var cfg = readCfg(root), map = cfg.map || {}, z = cfg.sizing;
    ['Stretch I', 'Stretch II', 'all'].forEach(function (k) { if (cfg.urls && cfg.urls[k]) cfg.urls[k] = keepView(cfg.urls[k], root); });
    var chips = $$('.a2-lp-chip', root), result = $('[data-result]', root);
    var label = $('[data-result-label]', root), name = $('[data-result-name]', root), range = $('[data-result-range]', root);
    var count = $('[data-count]', root), note = $('[data-boundary]', root), cta = $('[data-a2-lp-cta]', root);
    var resultLabel = (label && label.textContent) || 'Your Stretch';
    if (cta) cta.setAttribute('href', keepView(cta.getAttribute('href'), root));
    var cur = null;

    function apply(us, animate) {
      var row = map[String(us)]; if (!row) return null;
      var st = row.stretch;
      chips.forEach(function (c) { c.setAttribute('aria-pressed', c.getAttribute('data-size') === String(us) ? 'true' : 'false'); });
      var swap = function () {
        result.classList.remove('is-empty');
        if (label) label.textContent = resultLabel;
        name.textContent = st;
        if (range) range.textContent = (cfg.copy.range || {})[st] || '';
        result.classList.remove('is-fading');
      };
      if (animate && cur) { result.classList.add('is-fading'); setTimeout(swap, 150); } else swap();
      var show = (row.count || 0) >= (cfg.minCount || 0);
      if (count) {
        count.hidden = !show;
        if (show) count.textContent = localise(fill(cfg.copy.countLine, { count: row.count, size: us, stretch: st }), z);
      }
      if (note) note.hidden = (cfg.boundary || []).indexOf(String(us)) < 0;
      if (cta) {
        cta.textContent = fill(cfg.copy.cta, { stretch: st });
        cta.setAttribute('href', cfg.urls[st] || cfg.urls.all || '#collection');
      }
      cur = { us: String(us), stretch: st };
      return st;
    }
    if (result.classList.contains('is-empty')) cur = null;
    else { var pressed = $('.a2-lp-chip[aria-pressed="true"]', root); if (pressed) cur = { us: pressed.getAttribute('data-size'), stretch: (map[pressed.getAttribute('data-size')] || {}).stretch }; }

    var saved = lsGet(LS_USUAL);
    if (saved && map[saved] && (!cur || cur.us !== saved)) apply(saved, false);
    if (cur) stretchEvent(cur.stretch, cur.us, cfg.urls[cur.stretch], true);

    root.addEventListener('click', function (e) {
      var chip = e.target.closest('.a2-lp-chip');
      if (chip) {
        if (chip.getAttribute('aria-pressed') === 'true') return;
        var us = chip.getAttribute('data-size'), st = apply(us, !reduceMotion.matches);
        if (!st) return;
        lsSet(LS_USUAL, us);
        stretchEvent(st, us, cfg.urls[st], false);
        trackR(root, 'lp_matcher_size', { usual_size_us: us, stretch: st });
        reportSize(us, evMap(root)._report || 'discover_matcher', cfg.sizing);
        return;
      }
      if (e.target.closest('[data-a2-lp-cta]')) {
        if (cur) ssSet(SS_STRETCH, cur.stretch);
        trackR(root, 'lp_matcher_cta', { stretch: cur ? cur.stretch : '', usual_size_us: cur ? cur.us : '', method: !cur ? 'all' : (cfg.urls.source === 'filter' ? 'navigate' : 'fallback') });
      } else if (e.target.closest('[data-quiz-open]')) {
        trackR(root, 'lp_quiz_open', { source: 'matcher' });
      }
    });
  }

  /* ---------- Press: feature (Discover-1) and strip (Discover-2), 02-BEHAVIOUR §2 ---------- */
  function initPress(root) {
    var figs = $$('[data-a2-lp-press-fig]', root), n = figs.length;
    if (n < 2) return;
    var dots = $$('[data-a2-lp-press-dot]', root), logos = $$('[data-a2-lp-press-logo]', root);
    var perD = Math.max(1, Math.min(n, parseInt(root.dataset.perDesktop, 10) || 1));
    var secs = parseInt(root.dataset.autoplay, 10) || 0;
    var i = 0, timer = null, visible = false, held = false, hidden = document.hidden;
    var stack = $('[data-a2-lp-press-stack]', root);
    function per() { return mqDesktop.matches ? perD : 1; }
    function render() {
      var p = per();
      figs.forEach(function (f, k) {
        var pos = (k - i + n) % n, shown = pos < p;
        f.classList.toggle('is-active', k === i);
        f.classList.toggle('is-shown', shown);
        f.style.order = p > 1 ? pos : '';
        if (shown) f.removeAttribute('aria-hidden'); else f.setAttribute('aria-hidden', 'true');
      });
      dots.forEach(function (el, k) { el.classList.toggle('is-active', k === i); el.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      logos.forEach(function (el) {
        var k = +el.getAttribute('data-a2-lp-press-logo'), on = (k - i + n) % n < p;
        el.classList.toggle('is-active', on);
        if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    function go(k) { i = (k + n) % n; render(); }
    function play() {
      clearInterval(timer); timer = null;
      if (secs && visible && !held && !hidden && !reduceMotion.matches) timer = setInterval(function () { go(i + per()); }, secs * 1000);
    }
    function pubName(k) { var f = figs[k]; var img = f && f.querySelector('img'); return img ? img.alt : (f ? (f.querySelector('.a2-lp-pr__by, .a2-lp-pf__name') || {}).textContent || '' : ''); }
    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-a2-lp-press-logo], [data-a2-lp-press-dot]');
      if (!t) return;
      var isLogo = t.hasAttribute('data-a2-lp-press-logo');
      var k = +(isLogo ? t.getAttribute('data-a2-lp-press-logo') : t.getAttribute('data-a2-lp-press-dot'));
      trackR(root, 'lp_press_select', { publication: String(pubName(k)).replace(/^—\s*/, ''), source: isLogo ? 'logo' : 'dot' });
      if (t.tagName === 'A') return; // a block link opens the article
      go(k); play();
    });
    var onEnter = function () { held = true; play(); }, onLeave = function () { held = false; play(); };
    root.addEventListener('mouseenter', onEnter); root.addEventListener('mouseleave', onLeave);
    root.addEventListener('focusin', onEnter); root.addEventListener('focusout', onLeave);
    var io = new IntersectionObserver(function (es) { visible = es[0].isIntersecting; play(); });
    io.observe(root);
    var onVis = function () { hidden = document.hidden; play(); };
    document.addEventListener('visibilitychange', onVis);
    var onMq = function () { render(); play(); };
    mqDesktop.addEventListener('change', onMq);
    if (stack) bindSwipe(stack, function (dir) { go(i + dir * per()); play(); trackR(root, 'lp_press_select', { publication: String(pubName(i)).replace(/^—\s*/, ''), source: 'swipe' }); });
    render();
    root._a2lpCleanup = function () { clearInterval(timer); io.disconnect(); document.removeEventListener('visibilitychange', onVis); mqDesktop.removeEventListener('change', onMq); };
  }

  /* ---------- Range tabs (Discover-2, 02-BEHAVIOUR §3) ---------- */
  function initRange(root) {
    var tabs = $$('[data-a2-lp-tab]', root);
    if (!tabs.length) return;
    function select(n, focus, user) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-a2-lp-tab') === String(n);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focus) t.focus();
      });
      $$('[data-a2-lp-panel]', root).forEach(function (p) {
        var on = p.getAttribute('data-a2-lp-panel') === String(n);
        p.hidden = !on;
        if (on) p.scrollLeft = 0;
      });
      if (user) trackR(root, 'lp_range_tab', { stretch: n === 2 || n === '2' ? 'Stretch II' : 'Stretch I' });
    }
    // data-a2-start-saved: open on the saved usual size's Stretch (US 14 and up = Stretch II).
    if (root.hasAttribute('data-a2-start-saved')) {
      var sv = parseInt(lsGet(LS_USUAL), 10);
      if (sv >= 14 && tabs.some(function (t) { return t.getAttribute('data-a2-lp-tab') === '2'; })) select(2, false, false);
    }
    root.addEventListener('click', function (e) {
      var t = e.target.closest('[data-a2-lp-tab]');
      if (t && t.getAttribute('aria-selected') !== 'true') select(t.getAttribute('data-a2-lp-tab'), false, true);
    });
    root.addEventListener('keydown', function (e) {
      var t = e.target.closest('[data-a2-lp-tab]'); if (!t) return;
      var k = tabs.indexOf(t), next = null;
      if (e.key === 'ArrowRight') next = (k + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (k - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      if (next == null) return;
      e.preventDefault();
      select(tabs[next].getAttribute('data-a2-lp-tab'), true, true);
    });
    var onStretch = function (e) {
      if (!e.detail || e.detail.initial) return; // only a pick made on this page view
      select(e.detail.stretch === 'Stretch II' ? 2 : 1, false, false);
    };
    document.addEventListener('lp:stretch', onStretch);
    root._a2lpCleanup = function () { document.removeEventListener('lp:stretch', onStretch); };
  }

  /* ---------- Mobile sticky bar (02-BEHAVIOUR §5) ---------- */
  function initSticky(bar) {
    var btn = $('[data-a2-lp-sticky-btn]', bar);
    var hideInColl = bar.getAttribute('data-hide-in-collection') === 'true';
    var picked = null, shown = false, raf = 0;
    function actions() { return $('[data-a2-lp-hero-actions]'); }
    function coll() {
      var c = $('[data-a2-lp-collection]');
      if (c) return c;
      var g = document.getElementById('collection-grid');
      return g ? (g.closest('.shopify-section') || g) : null;
    }
    function update() {
      raf = 0;
      var acts = actions(), c = coll();
      var past = acts ? acts.getBoundingClientRect().bottom < 0 : window.scrollY > 400;
      var inColl = hideInColl && c ? c.getBoundingClientRect().top < window.innerHeight : false;
      var show = !mqDesktop.matches && past && !inColl;
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-shown', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) btn.removeAttribute('tabindex'); else btn.setAttribute('tabindex', '-1');
      document.body.style.paddingBottom = show ? bar.offsetHeight + 'px' : '';
    }
    var onScroll = function () { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    var onStretch = function (e) {
      var tpl = bar.getAttribute('data-label-picked');
      if (!tpl || !e.detail || !e.detail.stretch) return;
      picked = e.detail;
      btn.textContent = fill(tpl, { stretch: e.detail.stretch });
      if (e.detail.url) btn.setAttribute('href', e.detail.url);
    };
    document.addEventListener('lp:stretch', onStretch);
    bar.addEventListener('click', function (e) {
      if (!e.target.closest('[data-a2-lp-sticky-btn]')) return;
      if (picked) ssSet(SS_STRETCH, picked.stretch);
      track('lp_sticky_cta', { state: picked ? 'picked' : 'start', stretch: picked ? picked.stretch : '' });
    });
    update();
    bar._a2lpCleanup = function () {
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll);
      document.removeEventListener('lp:stretch', onStretch); document.body.style.paddingBottom = '';
    };
  }

  /* ---------- Click tracking: chips, links, photo hero ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-a2-lp-chip], [data-a2-lp-track], [data-a2-lp-hero-btn], [data-a2-lp-hero-card]');
    if (!t) return;
    if (t.hasAttribute('data-a2-lp-chip')) track('lp_chip', { label: t.getAttribute('data-a2-lp-chip') });
    else if (t.hasAttribute('data-a2-lp-track')) track(t.getAttribute('data-a2-lp-track'), t.hasAttribute('data-quiz-source') ? { source: t.getAttribute('data-quiz-source') } : {});
    else {
      var b = t.getAttribute('data-a2-lp-hero-btn') || t.getAttribute('data-a2-lp-hero-card');
      track(document.querySelector('[data-a2-lp-size]') ? 'lp_photo_hero_cta' : 'lp_hero_cta', { button: b });
    }
  });
  // Product clicks after a size-led CTA are tagged, to measure the hero / matcher's effect.
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a || a.closest('.ys.a2-lp')) return;
    var st = ssGet(SS_STRETCH);
    if (!st) return;
    var key = document.querySelector('[data-a2-lp-size]') ? 'from_size_hero' : 'from_matcher';
    var data = { stretch: st, url: a.getAttribute('href') }; data[key] = true;
    track('lp_product_click', data);
  });

  /* ---------- Sale page size bar (sections/a2-size-bar.liquid; sale-sizebar-handoff 02-BEHAVIOUR) ----------
     State rendering lives in window.A2SizeBar (the section's inline script, so a saved size shows before
     first paint). Here: taps → the collection's own Size filter (reload to the bar), same-Stretch taps
     without a reload, Clear, the saved-size link, analytics, the sticky strip, and re-syncing after the
     theme's AJAX filter updates (which rewrite the URL without ?view= or ys_quiz, so the quiz flag is
     kept in memory). */
  function initSizeBar(root) {
    var SB = window.A2SizeBar; if (!SB) return;
    var c = SB.cfg(root); if (!c) return;
    var quiz = !!root._sbQuiz;
    var p = c.param || 'filter.v.option.size';
    var V1 = c.values['Stretch I'], V2 = c.values['Stretch II'];
    function current() { return root._sbState || SB.derive(root, { quiz: quiz }); }
    function base() { var u = new URL(location.href); u.searchParams.delete('page'); u.searchParams.delete('ys_quiz'); u.hash = c.anchor || 'size-bar'; return u; }
    function out(u) { return keepView(u.pathname + u.search + u.hash); }
    // Apply / swap the Stretch value of the Size filter; other filters (colour, style …) are kept.
    function urlFor(stretch) {
      var u = base(), keep = u.searchParams.getAll(p).filter(function (v) { return v !== V1 && v !== V2; });
      u.searchParams.delete(p);
      keep.forEach(function (v) { u.searchParams.append(p, v); });
      if (stretch) u.searchParams.append(p, c.values[stretch]);
      return out(u);
    }
    function clearAllUrl() {
      var u = base(), keys = [];
      u.searchParams.forEach(function (v, k) { if (/^filter\./.test(k)) keys.push(k); });
      keys.forEach(function (k) { u.searchParams.delete(k); });
      return out(u);
    }
    // Filter the grid in place: tick the Stretch value in the theme's own filter form (it exists even when
    // the panel is closed) and fire the 'input' event the collection section listens for; the theme then
    // fetches and swaps the grid, updates the URL and fires theme:filters:init (→ resync). Returns false
    // when it can't (no form / value, or that Stretch has no products with the other active filters), so
    // the caller falls back to a reload.
    function filterForm() { return document.querySelector('[data-collection-filters-form]'); }
    function ajaxSetStretch(stretch) {
      var form = filterForm(); if (!form) return false;
      var boxes = $$('input[type="checkbox"]', form).filter(function (i) { return i.name === p; });
      var b1 = boxes.filter(function (i) { return i.value === V1; })[0], b2 = boxes.filter(function (i) { return i.value === V2; })[0];
      if (!b1 || !b2) return false;
      var target = stretch === 'Stretch I' ? b1 : stretch === 'Stretch II' ? b2 : null;
      if (target && target.disabled) return false;
      b1.checked = target === b1; b2.checked = target === b2;
      root.classList.add('is-loading');
      form.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    }
    function ajaxClearAll() {
      if (/[?&]filter\.v\.price/.test(location.search)) return false; // price slider: leave it to a reload
      var form = filterForm(); if (!form) return false;
      $$('input[type="checkbox"]', form).forEach(function (i) { if (/^filter\./.test(i.name)) i.checked = false; });
      root.classList.add('is-loading');
      form.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    }
    // The theme rewrites the URL without ?view=; put it back so a refresh / shared link keeps the template.
    var startView = (/(?:^|[?&])view=([^&#]+)/.exec(location.search) || [])[1];
    function restoreView() {
      if (!startView || /(?:^|[?&])view=/.test(location.search)) return;
      try {
        var u = new URL(location.href); u.searchParams.set('view', decodeURIComponent(startView));
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      } catch (e) {}
    }
    function scrollChip(smoothly) {
      var row = $('.a2-sb__chips', root); if (!row || row.scrollWidth <= row.clientWidth) return;
      var on = $('.is-applied', row) || $('.is-saved', row); if (!on) return;
      var chips = Array.prototype.slice.call(row.children), i = chips.indexOf(on);
      row.scrollTo({ left: Math.max(0, (i - 1) * (on.getBoundingClientRect().width + 6)), behavior: smoothly ? smooth() : 'auto' });
    }
    function setHeader() { root.style.setProperty('--sb-header', headerOffset() + 'px'); }
    // "3 filters applied" next to the collection's Filters toggle (the panel may be closed by default,
    // and the theme's own active-filter chips are switched off). Counted from the URL, so it stays right
    // after the theme's AJAX filter updates, which also re-render the toggle (hence re-applied on resync).
    function markFilters() {
      var title = document.getElementById('filters-heading');
      if (!title) return;
      var n = 0, price = false;
      new URL(location.href).searchParams.forEach(function (v, k) {
        if (!/^filter\./.test(k)) return;
        if (/price/.test(k)) { price = true; return; }
        n++;
      });
      if (price) n++;
      var el = title.querySelector('.a2-sb-filters-count');
      var tpl = n === 1 ? (c.copy.filtersCountOne || c.copy.filtersCount) : c.copy.filtersCount;
      if (!n || !tpl) { if (el) el.remove(); return; }
      if (!el) { el = document.createElement('span'); el.className = 'a2-sb-filters-count'; title.appendChild(el); }
      el.textContent = fill(tpl, { count: n });
    }
    function pick(us, how) {
      var st0 = current(), stretch = c.map[String(us)];
      if (!stretch || (st0.applied === String(us) && st0.stretch === stretch)) return;
      try { localStorage.setItem(LS_USUAL, String(us)); } catch (e) {}
      track(how === 'saved' ? 'sale_size_saved_apply' : 'sale_size_pick', { usual_size_us: String(us), stretch: stretch, had_saved_size: !!st0.saved, template: c.template });
      if (st0.stretch === stretch) {
        // Same Stretch already filtering the grid: only the chip, saved size and result line change.
        reportSize(us, 'sale_size_bar', c.sizing);
        SB.render(root, Object.assign({}, st0, { applied: String(us), saved: String(us) }));
        scrollChip(true);
        return;
      }
      if (ajaxSetStretch(stretch)) {
        reportSize(us, 'sale_size_bar', c.sizing);
        // Show the pick straight away; the count fills in when the theme's render lands (resync).
        SB.render(root, Object.assign({}, st0, { applied: String(us), saved: String(us), stretch: stretch, count: '' }));
        scrollChip(true);
        return;
      }
      root.classList.add('is-loading');
      var gone = false, go = function () { if (gone) return; gone = true; location.href = urlFor(stretch); };
      reportSize(us, 'sale_size_bar', c.sizing, true).then(go, go);
      setTimeout(go, 900);
    }
    root.addEventListener('click', function (e) {
      var chip = e.target.closest('.a2-sb__chip');
      if (chip) { e.preventDefault(); pick(chip.getAttribute('data-size'), 'chip'); return; }
      if (e.target.closest('[data-a2-sb-saved-apply]')) { e.preventDefault(); var sv = current().saved; if (sv) pick(sv, 'saved'); return; }
      if (e.target.closest('[data-a2-sb-clear]')) {
        e.preventDefault(); track('sale_size_clear', { from: 'picked', template: c.template });
        if (!ajaxSetStretch('')) location.href = urlFor('');
        return;
      }
      if (e.target.closest('[data-a2-sb-quiz-clear]')) {
        e.preventDefault(); track('sale_size_clear', { from: 'quiz', template: c.template });
        if (ajaxClearAll()) quiz = false; else location.href = clearAllUrl();
        return;
      }
      var qb = e.target.closest('[data-a2-sb-quiz-btn]');
      if (qb) track('sale_quiz_open', { state: quiz && current().quiz ? 'quiz' : (root.getAttribute('data-state') || 'start'), surface: qb.classList.contains('a2-sb__quiz--sticky') ? 'sticky' : 'bar', template: c.template });
    });
    // The page has no product count element (it's commented out in the collection section), so after an
    // in-place filter the bar asks Shopify for its own section at the current URL (Section Rendering API)
    // and takes the count, active Stretch and filter labels from the config it renders.
    var secEl = root.closest('.shopify-section'), secId = secEl && secEl.id ? secEl.id.replace(/^shopify-section-/, '') : '';
    var reqN = 0;
    function refreshFromServer() {
      if (!secId) return;
      var n = ++reqN, u = new URL(location.href);
      u.searchParams.set('section_id', secId); u.hash = '';
      fetch(u.pathname + u.search, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (n !== reqN) return; // a newer filter change is in flight
          var m = html.match(/<script type="application\/json" data-a2-sb-config>([\s\S]*?)<\/script>/);
          if (!m) return;
          var fresh = JSON.parse(m[1]);
          var st = SB.derive(root, { quiz: quiz, stretch: fresh.active || '', count: fresh.count, tags: fresh.activeFilters || [] });
          if (quiz && !st.quiz) quiz = false;
          SB.render(root, st);
          scrollChip(false);
          markFilters();
        })
        .catch(function () { /* keep the URL-derived state */ });
    }
    // After the theme's AJAX filter render (or back/forward), rebuild the state from the URL + count.
    function resync() {
      root.classList.remove('is-loading');
      restoreView();
      var u = new URL(location.href), vals = u.searchParams.getAll(p);
      var has1 = vals.indexOf(V1) > -1, has2 = vals.indexOf(V2) > -1;
      var stretch = has1 && !has2 ? 'Stretch I' : has2 && !has1 ? 'Stretch II' : '';
      var el = document.querySelector('[data-products-count]'), m = el && el.textContent.match(/\d+/);
      var count = m ? +m[0] : '';
      var tags = [];
      u.searchParams.forEach(function (v, k) { if (/^filter\./.test(k) && !/price/.test(k) && !/^gid:/.test(v)) tags.push(v); });
      var st = SB.derive(root, { quiz: quiz, stretch: stretch, count: (stretch || quiz) ? count : '', tags: tags });
      if (quiz && !st.quiz) quiz = false; // every quiz filter removed → back to the normal states
      SB.render(root, st);
      scrollChip(false);
      markFilters();
      if (count === '') refreshFromServer();
    }
    var onFilters = function () { setTimeout(resync, 60); };
    var onResize = function () { clearTimeout(root._sbFit); root._sbFit = setTimeout(function () { SB.fit(root); }, 120); };
    window.addEventListener('resize', onResize);
    document.addEventListener('theme:filters:init', onFilters);
    window.addEventListener('popstate', onFilters);
    // Land with the bar under the sticky header after a filter reload (#size-bar).
    setHeader();
    if (location.hash === '#' + (c.anchor || 'size-bar')) {
      var land = function () { setHeader(); root.scrollIntoView({ block: 'start' }); };
      land(); window.addEventListener('load', land, { once: true });
    }
    // Sticky strip (option): over the grid only, once the bar has scrolled away.
    var strip = $('[data-a2-sb-sticky]', root), io = null;
    if (strip && 'IntersectionObserver' in window) {
      var grid = document.getElementById('collection-grid');
      grid = grid ? (grid.closest('.shopify-section') || grid) : null;
      var barOut = false, gridIn = false;
      var show = function () {
        var on = barOut && gridIn;
        strip.classList.toggle('is-shown', on);
        strip.setAttribute('aria-hidden', on ? 'false' : 'true');
        $$('button', strip).forEach(function (b) { if (on) b.removeAttribute('tabindex'); else b.setAttribute('tabindex', '-1'); });
        if (on) setHeader();
      };
      io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.target === root) barOut = !e.isIntersecting && e.boundingClientRect.top < 0;
          else gridIn = e.isIntersecting;
        });
        show();
      }, { rootMargin: '-60px 0px 0px 0px' });
      io.observe(root); if (grid) io.observe(grid);
    }
    scrollChip(false);
    markFilters();
    track('sale_bar_view', { initial_state: quiz && current().quiz ? 'quiz' : (root.getAttribute('data-state') || 'start'), template: c.template });
    root._a2lpCleanup = function () {
      document.removeEventListener('theme:filters:init', onFilters);
      window.removeEventListener('popstate', onFilters);
      window.removeEventListener('resize', onResize);
      if (io) io.disconnect();
    };
  }

  /* ---------- Init / theme editor ---------- */
  function init(scope) {
    scope = scope || document;
    $$('[data-a2-lp-size]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initSizeHero(r); } });
    $$('[data-a2-lp-matcher]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initMatcher(r); } });
    $$('[data-a2-lp-press]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initPress(r); } });
    $$('[data-a2-lp-range]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initRange(r); } });
    $$('[data-a2-lp-sticky]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initSticky(r); } });
    $$('[data-a2-size-bar]', scope).forEach(function (r) { if (!r._a2lp) { r._a2lp = true; initSizeBar(r); } });
    $$('.ys.a2-lp-ci, .ys.a2-lp-hero', scope).forEach(keepViewLinks);
    initVideos(scope);
    setHeaderVar();
  }
  window.A2LP = { init: init, localise: localise };

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    $$('.ys.a2-lp, [data-a2-size-bar]', e.target).forEach(function (r) { if (r._a2lpCleanup) r._a2lpCleanup(); });
  });
  window.addEventListener('resize', function () { clearTimeout(window._a2lpR); window._a2lpR = setTimeout(setHeaderVar, 150); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // A #anchor in the URL (e.g. a filtered link's #collection) lands below the sticky header.
  window.addEventListener('load', function () {
    var id = location.hash.slice(1);
    if (id && document.getElementById(id) && document.getElementById(id).closest('.ys.a2-lp')) {
      setHeaderVar();
      document.getElementById(id).scrollIntoView({ block: 'start' });
    }
  });
})();
